import { IWedding_Website_Section_GiftListField } from '@bridebook/models/source/models/Weddings.types';
import { Box } from '@bridebook/ui';
import { ms } from '@bridebook/ui/src/fela-utils/ms';
import {
  WebsiteTileVariant,
  WeddingWebsiteTileComponent,
} from '@bridebook/web/components/wedding-website/view-mode/partials/tile-component/wedding-website-tile-component';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch } from '@bridebook/web/lib/utils';
import { interactedWithWeddingWebsiteAnalyticsButtonClick } from '@bridebook/web/lib/wedding-website/analytics';
import { GIFT_LIST_DECORATIVE_IMAGE } from '@bridebook/web/lib/wedding-website/gift-list/constants';
import { Palettes } from '@bridebook/web/lib/wedding-website/types';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef } from 'react';
import { useFela } from 'react-fela';
import styles from './wedding-website-gift-list.style';
import { FloatingEditButton } from '@bridebook/web/components/wedding-website/view-mode/partials/floating-edit-button/floating-edit-button';
import { useEditorSyncContext } from '@bridebook/web/components/wedding-website/main/desktop/editor-sync-context';
import { buildImageSrc } from '@bridebook/toolbox/src/buildImageUrl';

interface IProps {
  section: IWedding_Website_Section_GiftListField;
  variant: WebsiteTileVariant;
  palette: Palettes;
}

export const WeddingWebsiteGiftList = ({ variant, palette, section }: IProps) => {
  const { css } = useFela();
  const dispatch = useDispatch();
  const { t } = useTranslation('weddingWebsite');
  const { triggerDetailsSync, registerPreviewRef } = useEditorSyncContext();
  const tileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerPreviewRef('giftList', tileRef.current);
  }, [registerPreviewRef]);

  const handleEditClick = useCallback(() => {
    triggerDetailsSync('giftList');
    dispatch(
      interactedWithWeddingWebsiteAnalyticsButtonClick({
        section: 'giftList',
        element: 'editButton',
      }),
    );
  }, [dispatch, triggerDetailsSync]);

  return (
    <div ref={tileRef} className="group w-full">
      <WeddingWebsiteTileComponent variant={variant} palette={palette} style={styles.tileComponent}>
        <Box style={styles.innerWrapper}>
          <Box style={styles.firstColumn}>
            <WeddingWebsiteTileComponent.Title style={styles.title}>
              {t('previewMode.tile.giftList.title')}
            </WeddingWebsiteTileComponent.Title>
            <img
              src={buildImageSrc(GIFT_LIST_DECORATIVE_IMAGE, { w: 320 })}
              className={css(ms(styles.image, styles.imageMobile))}
            />
            {section.data.message && (
              <WeddingWebsiteTileComponent.Text style={styles.body}>
                {section.data.message}
              </WeddingWebsiteTileComponent.Text>
            )}

            <a href={section.data.url} target="_blank" rel="noopener noreferrer">
              <WeddingWebsiteTileComponent.Button
                variant={palette === Palettes.Bridebook ? 'secondary' : 'primary'}
                styleWrapper={styles.button}
                disabled={!section.data.url}>
                {t('previewMode.tile.giftList.viewGiftList')}
              </WeddingWebsiteTileComponent.Button>
            </a>
          </Box>

          <Box style={ms(styles.secondColumn, styles.imageDesktopWrapper)}>
            <img
              src={buildImageSrc(GIFT_LIST_DECORATIVE_IMAGE, { w: 320 })}
              className={css(ms(styles.image, styles.imageDesktop))}
            />
          </Box>
        </Box>
        <FloatingEditButton
          href={UrlHelper.website.weddingDetails.sectionAnchor('giftList')}
          onClick={handleEditClick}
        />
      </WeddingWebsiteTileComponent>
    </div>
  );
};
